import { useEffect, useMemo, useState } from "react";
import api from "../services/api";
import { settingsApi } from "../services/settingsApi";
import FeedbackModal from "../components/ui/FeedbackModal";
import { useTheme } from "../context/ThemeContext";
import {
  FiUser,
  FiEye,
  FiEyeOff,
  FiSun,
  FiMoon,
  FiCreditCard,
  FiDollarSign,
  FiPercent,
  FiType,
  FiSave,
  FiShield,
} from "react-icons/fi";

function Card({ children, className = "" }) {
  return (
    <div
      className={`rounded-3xl border border-slate-100 bg-white shadow-sm transition-colors duration-300 dark:border-slate-800 dark:bg-slate-900 ${className}`}
    >
      {children}
    </div>
  );
}

function Toggle({ enabled, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!enabled)}
      className={`relative h-7 w-14 rounded-full transition ${
        enabled ? "bg-orange-500" : "bg-slate-300 dark:bg-slate-600"
      }`}
    >
      <span
        className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition ${
          enabled ? "left-7" : "left-0.5"
        }`}
      />
    </button>
  );
}

const currencyOptions = ["USD", "GBP", "PKR", "EUR", "AED", "SAR"];

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function Settings() {
  const { theme, setTheme } = useTheme();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [user, setUser] = useState(null);

  const [name, setName] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [settings, setSettings] = useState({
    companyName: "",
    companyEmail: "",
    companyPhone: "",
    companyAddress: "",
    logoDataUrl: "",
    invoiceShowLogo: true,
    invoiceShowTitle: true,
    invoiceTitle: "INVOICE",
    invoiceTemplate: "modern",
    invoiceCurrency: "USD",
    invoiceTaxEnabled: false,
    invoiceTaxRate: 0,
    invoiceDiscountEnabled: false,
    invoiceDiscountRate: 0,
  });

  const [feedback, setFeedback] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
  });

  useEffect(() => {
    const load = async () => {
      try {
        const meRes = await api.get("/auth/me");
        const freshUser = meRes.data?.user || null;
        setUser(freshUser);
        setName(freshUser?.name || "");

        const settingsRes = await settingsApi.getMy();
        const s = settingsRes.data?.settings || {};
        setSettings((prev) => ({
          ...prev,
          ...s,
          invoiceShowLogo: Boolean(s.invoiceShowLogo),
          invoiceShowTitle: Boolean(s.invoiceShowTitle),
          invoiceTaxEnabled: Boolean(s.invoiceTaxEnabled),
          invoiceDiscountEnabled: Boolean(s.invoiceDiscountEnabled),
        }));

        localStorage.setItem("user", JSON.stringify(freshUser));
      } catch (err) {
        const savedUser = localStorage.getItem("user");
        if (savedUser) {
          try {
            const parsed = JSON.parse(savedUser);
            setUser(parsed);
            setName(parsed.name || "");
          } catch {
            setUser(null);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const passwordStrength = useMemo(() => {
    const issues = [];
    const password = newPassword || "";

    if (password && password.length < 8) issues.push("At least 8 characters");
    if (password && !/[a-z]/.test(password)) issues.push("One lowercase letter");
    if (password && !/[A-Z]/.test(password)) issues.push("One uppercase letter");
    if (password && !/[0-9]/.test(password)) issues.push("One number");
    if (password && !/[^A-Za-z0-9]/.test(password)) issues.push("One special character");

    let label = "Weak";
    if (issues.length <= 1 && password.length >= 8) label = "Strong";
    else if (issues.length <= 3) label = "Medium";

    return { issues, label };
  }, [newPassword]);

  const closeFeedback = () => setFeedback((prev) => ({ ...prev, open: false }));

  const onLogoChange = async (file) => {
    if (!file) return;
    const dataUrl = await readFileAsDataUrl(file);
    setSettings((prev) => ({ ...prev, logoDataUrl: dataUrl }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const passwordFlow = oldPassword || newPassword || confirmPassword;

      if (passwordFlow) {
        if (!oldPassword || !newPassword || !confirmPassword) {
          throw new Error("Old password, new password, and confirm password are required");
        }
        if (newPassword !== confirmPassword) {
          throw new Error("New password and confirm password do not match");
        }
      }

      const profilePayload = { name: name.trim() };
      if (passwordFlow) {
        profilePayload.oldPassword = oldPassword;
        profilePayload.newPassword = newPassword;
        profilePayload.confirmPassword = confirmPassword;
      }

      const appSettingsPayload = {
        ...settings,
        invoiceTitle: settings.invoiceTitle || "INVOICE",
        invoiceCurrency: settings.invoiceCurrency || "USD",
        invoiceTaxRate: Number(settings.invoiceTaxRate || 0),
        invoiceDiscountRate: Number(settings.invoiceDiscountRate || 0),
      };

      const [profileRes, settingsRes] = await Promise.all([
        api.put("/auth/update-profile", profilePayload),
        settingsApi.updateMy(appSettingsPayload),
      ]);

      const updatedUser = profileRes.data?.user || user;
      if (updatedUser) {
        localStorage.setItem("user", JSON.stringify(updatedUser));
        setUser(updatedUser);
      }

      setSettings((prev) => ({
        ...prev,
        ...(settingsRes.data?.settings || {}),
      }));

      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");

      setFeedback({
        open: true,
        type: "success",
        title: "Saved",
        message: "Profile and invoice settings updated successfully.",
      });
    } catch (err) {
      setFeedback({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || err.message || "Something went wrong",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          Loading settings...
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <FeedbackModal
        open={feedback.open}
        type={feedback.type}
        title={feedback.title}
        message={feedback.message}
        onClose={closeFeedback}
      />

      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
          Settings
        </h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Update your profile, password, invoice settings, and appearance in one place.
        </p>
      </div>

      <form
        onSubmit={handleSave}
        className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_420px]"
      >
        <div className="space-y-6">
          <Card className="p-6">
            <div className="flex items-center gap-3">
              <div className="grid h-11 w-11 place-items-center rounded-2xl bg-orange-100 text-orange-600 dark:bg-orange-950 dark:text-orange-300">
                <FiUser />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Profile
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Update your account details
                </p>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Full Name
                </label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-orange-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Email
                </label>
                <input
                  value={user?.email || ""}
                  disabled
                  className="w-full rounded-2xl border border-slate-200 bg-slate-100 px-4 py-3 text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
                />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-3">
              <div className="grid h-11 w-11 place-items-center rounded-2xl bg-orange-100 text-orange-600 dark:bg-orange-950 dark:text-orange-300">
                <FiShield />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Password
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Change your account password
                </p>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Old Password
                </label>
                <div className="relative">
                  <input
                    type={showOldPassword ? "text" : "password"}
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 pr-12 text-slate-900 outline-none transition focus:border-orange-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowOldPassword((v) => !v)}
                    className="absolute inset-y-0 right-3 flex items-center text-slate-500 dark:text-slate-400"
                  >
                    {showOldPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 pr-12 text-slate-900 outline-none transition focus:border-orange-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword((v) => !v)}
                    className="absolute inset-y-0 right-3 flex items-center text-slate-500 dark:text-slate-400"
                  >
                    {showNewPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>

                {newPassword ? (
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    Strength: <span className="font-semibold">{passwordStrength.label}</span>
                    {passwordStrength.issues.length > 0
                      ? ` — ${passwordStrength.issues.join(", ")}`
                      : ""}
                  </p>
                ) : null}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 pr-12 text-slate-900 outline-none transition focus:border-orange-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    className="absolute inset-y-0 right-3 flex items-center text-slate-500 dark:text-slate-400"
                  >
                    {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-3">
              <div className="grid h-11 w-11 place-items-center rounded-2xl bg-orange-100 text-orange-600 dark:bg-orange-950 dark:text-orange-300">
                <FiCreditCard />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Invoice Settings
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  These values will be used by your invoice form and print view
                </p>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="md:col-span-2 flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4 dark:border-slate-800 dark:bg-slate-950">
                <div>
                  <h3 className="font-semibold text-slate-800 dark:text-slate-100">
                    Add a logo
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Show your brand logo on invoices
                  </p>
                </div>
                <Toggle
                  enabled={settings.invoiceShowLogo}
                  onChange={(v) =>
                    setSettings((prev) => ({ ...prev, invoiceShowLogo: v }))
                  }
                />
              </div>

              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Logo
                </label>
                <div className="flex items-center gap-4">
                  <label className="cursor-pointer rounded-2xl border border-dashed border-slate-300 px-4 py-3 text-sm text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) await onLogoChange(file);
                      }}
                    />
                    Upload logo
                  </label>

                  {settings.logoDataUrl ? (
                    <img
                      src={settings.logoDataUrl}
                      alt="Logo preview"
                      className="h-12 w-12 rounded-xl border border-slate-200 object-cover dark:border-slate-700"
                    />
                  ) : (
                    <div className="text-sm text-slate-400 dark:text-slate-500">
                      No logo uploaded
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Invoice title
                </label>
                <input
                  value={settings.invoiceTitle}
                  onChange={(e) =>
                    setSettings((prev) => ({ ...prev, invoiceTitle: e.target.value }))
                  }
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-orange-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                  placeholder="INVOICE"
                />
              </div>

              <div className="flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4 dark:border-slate-800 dark:bg-slate-950">
                <div>
                  <h3 className="font-semibold text-slate-800 dark:text-slate-100">
                    Show invoice title
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Display the title on printed invoices
                  </p>
                </div>
                <Toggle
                  enabled={settings.invoiceShowTitle}
                  onChange={(v) =>
                    setSettings((prev) => ({ ...prev, invoiceShowTitle: v }))
                  }
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Currency
                </label>
                <select
                  value={settings.invoiceCurrency}
                  onChange={(e) =>
                    setSettings((prev) => ({ ...prev, invoiceCurrency: e.target.value }))
                  }
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-orange-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                >
                  {currencyOptions.map((cur) => (
                    <option key={cur} value={cur}>
                      {cur}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Template
                </label>
                <select
                  value={settings.invoiceTemplate}
                  onChange={(e) =>
                    setSettings((prev) => ({ ...prev, invoiceTemplate: e.target.value }))
                  }
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-orange-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                >
                  <option value="modern">Modern</option>
                  <option value="classic">Classic</option>
                  <option value="minimal">Minimal</option>
                </select>
              </div>

              <div className="flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4 dark:border-slate-800 dark:bg-slate-950">
                <div>
                  <h3 className="font-semibold text-slate-800 dark:text-slate-100">
                    Tax
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Enable tax fields on invoice
                  </p>
                </div>
                <Toggle
                  enabled={settings.invoiceTaxEnabled}
                  onChange={(v) =>
                    setSettings((prev) => ({ ...prev, invoiceTaxEnabled: v }))
                  }
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Tax rate (%)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={settings.invoiceTaxRate}
                  onChange={(e) =>
                    setSettings((prev) => ({ ...prev, invoiceTaxRate: e.target.value }))
                  }
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-orange-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                />
              </div>

              <div className="flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4 dark:border-slate-800 dark:bg-slate-950">
                <div>
                  <h3 className="font-semibold text-slate-800 dark:text-slate-100">
                    Discount
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Enable discount fields on invoice
                  </p>
                </div>
                <Toggle
                  enabled={settings.invoiceDiscountEnabled}
                  onChange={(v) =>
                    setSettings((prev) => ({ ...prev, invoiceDiscountEnabled: v }))
                  }
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Discount rate (%)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={settings.invoiceDiscountRate}
                  onChange={(e) =>
                    setSettings((prev) => ({ ...prev, invoiceDiscountRate: e.target.value }))
                  }
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-orange-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Company name
                </label>
                <input
                  value={settings.companyName}
                  onChange={(e) =>
                    setSettings((prev) => ({ ...prev, companyName: e.target.value }))
                  }
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-orange-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                  placeholder="Your company"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Company email
                </label>
                <input
                  value={settings.companyEmail}
                  onChange={(e) =>
                    setSettings((prev) => ({ ...prev, companyEmail: e.target.value }))
                  }
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-orange-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                  placeholder="company@example.com"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Company phone
                </label>
                <input
                  value={settings.companyPhone}
                  onChange={(e) =>
                    setSettings((prev) => ({ ...prev, companyPhone: e.target.value }))
                  }
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-orange-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                  placeholder="+1..."
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Company address
                </label>
                <textarea
                  value={settings.companyAddress}
                  onChange={(e) =>
                    setSettings((prev) => ({ ...prev, companyAddress: e.target.value }))
                  }
                  rows="3"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-orange-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                  placeholder="Address..."
                />
              </div>
            </div>
          </Card>
        </div>

        <div className="flex flex-col space-y-6 self-start xl:sticky xl:top-6">
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Preview
            </h2>

            <div className="mt-4 rounded-3xl border border-slate-100 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950">
              {settings.invoiceShowLogo && settings.logoDataUrl ? (
                <img
                  src={settings.logoDataUrl}
                  alt="Logo"
                  className="mb-4 h-16 w-16 rounded-2xl object-cover"
                />
              ) : null}

              {settings.invoiceShowTitle ? (
                <h3 className="text-2xl font-bold tracking-wide text-slate-900 dark:text-slate-100">
                  {settings.invoiceTitle || "INVOICE"}
                </h3>
              ) : null}

              <div className="mt-4 text-sm text-slate-600 dark:text-slate-300">
                <div className="mt-2 flex items-center gap-2">
                  <FiDollarSign /> Currency: {settings.invoiceCurrency}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <FiPercent /> Tax:{" "}
                  {settings.invoiceTaxEnabled ? `${settings.invoiceTaxRate}%` : "Off"}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <FiType /> Template: {settings.invoiceTemplate}
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-3">
              <div className="grid h-11 w-11 place-items-center rounded-2xl bg-blue-100 text-blue-600 dark:bg-blue-950 dark:text-blue-300">
                <FiSun />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Appearance
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Control the visual style of your dashboard
                </p>
              </div>
            </div>

            <div className="mt-6 space-y-3">
              <button
                type="button"
                onClick={() => setTheme("light")}
                className={`flex w-full items-center justify-between rounded-2xl border px-5 py-4 text-left transition ${
                  theme === "light"
                    ? "border-blue-300 bg-blue-50 dark:bg-slate-800"
                    : "border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950"
                }`}
              >
                <div>
                  <div className="font-semibold text-slate-900 dark:text-slate-100">
                    Light Mode
                  </div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">
                    Bright and clean interface
                  </div>
                </div>
                <FiSun className="text-xl text-orange-500" />
              </button>

              <button
                type="button"
                onClick={() => setTheme("dark")}
                className={`flex w-full items-center justify-between rounded-2xl border px-5 py-4 text-left transition ${
                  theme === "dark"
                    ? "border-blue-300 bg-blue-50 dark:bg-slate-800"
                    : "border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950"
                }`}
              >
                <div>
                  <div className="font-semibold text-slate-900 dark:text-slate-100">
                    Dark Mode
                  </div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">
                    Reduce glare and eye strain
                  </div>
                </div>
                <FiMoon className="text-xl text-slate-600 dark:text-slate-300" />
              </button>
            </div>
          </Card>

          <button
            type="submit"
            disabled={saving}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-orange-500 px-5 py-4 font-semibold text-white transition hover:bg-orange-600 disabled:opacity-60"
          >
            <FiSave />
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
}